<script setup lang="ts">
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useQuery } from '@tanstack/vue-query';
import { UiButton, UiCard, UiInfoBox, UiText, useApi } from '@shopexperts/shared';
import PageHeader from '@/app/components/common/PageHeader.vue';
import ReviewForm, { type ReviewFormValues } from '@/app/components/reviews/ReviewForm.vue';
import type { ExpertData } from '@/app/components/reviews/ExpertDetailsCard.vue';
import { useSubmitOrganicReview } from '@/app/composables/reviews/useSubmitOrganicReview';

const route = useRoute();
const router = useRouter();
const api = useApi();

const expertSlug = computed(() => typeof route.params.expertSlug === 'string' ? route.params.expertSlug : '');
const {
  data: profile,
  isLoading: isLoadingProfile,
  error: profileError,
} = useQuery({
  queryKey: computed(() => ['public-expert-profile', expertSlug.value] as const),
  enabled: computed(() => expertSlug.value.length > 0),
  retry: false,
  queryFn: () => api.public.expert.getProfile({ params: { slug: expertSlug.value } }),
});

const expertData = computed<ExpertData | null>(() => {
  if (!profile.value) return null;
  return {
    name: profile.value.name,
    slug: profile.value.slug,
    type: profile.value.type === 'agency' ? 'agency' : 'freelancer',
    profile_picture: profile.value.avatar,
    role: profile.value.title,
  };
});
const canReceiveReviews = computed(() => (
  profile.value as (typeof profile.value & { can_receive_reviews?: boolean })
)?.can_receive_reviews === true);

const {
  submitReview,
  isSubmitting,
  isSuccess,
  errorMessage,
} = useSubmitOrganicReview(() => profile.value?.id);

async function handleSubmit(values: ReviewFormValues) {
  try {
    await submitReview({
      quality: values.quality,
      communication: values.communication,
      timelines: values.timelines,
      value_for_money: values.value_for_money,
      review_text: values.review_text,
      is_anonymous: values.is_anonymous,
      project_value_range_id: values.project_value_range_id ?? undefined,
    });
  } catch {
    // The mutation keeps the form mounted and exposes the mapped error below.
  }
}

function returnToReviews() {
  router.push('/client/reviews');
}
</script>

<template>
  <main class="flex-1 overflow-y-auto bg-secondary p-4 font-light md:p-8">
    <PageHeader
      title="Write a Review"
      description="Share your direct experience with this expert. Your review will be moderated before publication."
    />

    <div class="mx-auto w-full max-w-[640px]">
      <UiCard v-if="isLoadingProfile" padding="lg">
        <div class="flex flex-col items-center gap-3 py-12" role="status">
          <div class="h-10 w-10 animate-spin rounded-full border-2 border-se-gray-300 border-t-se-primary" />
          <UiText as="p" variant="p" text="Loading expert details..." />
        </div>
      </UiCard>

      <UiInfoBox
        v-else-if="profileError || !expertData"
        variant="error"
        title="Expert details could not be loaded"
      >
        <UiButton variant="outline" label="Back to Reviews" @click="returnToReviews" />
      </UiInfoBox>

      <UiInfoBox
        v-else-if="!canReceiveReviews"
        variant="error"
        title="This expert is not accepting direct reviews"
      >
        <UiButton variant="outline" label="Back to Reviews" @click="returnToReviews" />
      </UiInfoBox>

      <UiCard v-else padding="lg">
        <div class="flex flex-col gap-4">
          <UiInfoBox v-if="errorMessage" variant="error" title="Review could not be submitted">
            <UiText as="p" variant="p-small" :text="errorMessage" />
          </UiInfoBox>

          <ReviewForm
            :expert="expertData"
            :show-close-button="false"
            :is-submitting="isSubmitting"
            :is-success="isSuccess"
            title="Review **this expert**"
            description="The expert is locked from the public profile you selected. Complete every rating and add details about your experience."
            success-title="Review submitted for moderation"
            success-description="Thank you. Your review is pending moderation and will appear publicly only after approval."
            @submit="handleSubmit"
          />
        </div>
      </UiCard>
    </div>
  </main>
</template>
